import { useCallback, useEffect, useRef } from 'react'
import { useProgress } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import type { Object3D } from 'three'

type Props = {
  readyKey?: string | number
  onReady?: () => void
  mode?: 'selection' | 'combat'
}

function combatCoreReady(scene:Object3D){
  let fighterCount=0
  let fighterGreen=0
  let structuralAuthorities=0
  scene.traverse(node=>{
    const integrity=node.userData.inyronRuntimeIntegrity as {green?:boolean}|undefined
    if(integrity){fighterCount+=1;if(integrity.green===true)fighterGreen+=1}
    if(node.name.startsWith('arena-structural-authority:'))structuralAuthorities+=1
  })
  return fighterCount>=2
    &&fighterGreen===fighterCount
    &&structuralAuthorities===1
    &&Boolean(scene.userData.inyronConstructionLogic)
    &&Boolean(scene.userData.inyronScenarioArchitectureBound)
}

export function GpuWarmup({ readyKey = 'default', onReady, mode = 'combat' }: Props = {}) {
  const gl = useThree((state) => state.gl)
  const scene = useThree((state) => state.scene)
  const camera = useThree((state) => state.camera)
  const { active, loaded, total } = useProgress()
  const reportedKey = useRef<string | number | null>(null)
  const progressRef = useRef({ active, loaded, total })
  progressRef.current = { active, loaded, total }

  const reportReady = useCallback(() => {
    if (reportedKey.current === readyKey) return
    reportedKey.current = readyKey
    requestAnimationFrame(() => onReady?.())
  }, [onReady, readyKey])

  // Never perform a synchronous shader compile on the gameplay thread.
  useEffect(() => {
    let cancelled = false
    const hardWatchdog = window.setTimeout(() => {
      const progress = progressRef.current
      const idle = !progress.active && (progress.total === 0 || progress.loaded >= progress.total)
      if (!cancelled && reportedKey.current !== readyKey && idle) reportReady()
    }, mode === 'selection' ? 1800 : 7000)
    return () => {
      cancelled = true
      window.clearTimeout(hardWatchdog)
    }
  }, [mode, readyKey, reportReady])

  useEffect(() => {
    if (mode !== 'selection' || active || (total > 0 && loaded < total)) return
    let cancelled = false
    let frame: number | null = null
    let reportFrame: number | null = null
    frame = requestAnimationFrame(() => {
      reportFrame = requestAnimationFrame(() => {
        if (!cancelled) reportReady()
      })
    })
    return () => {
      cancelled = true
      if (frame !== null) cancelAnimationFrame(frame)
      if (reportFrame !== null) cancelAnimationFrame(reportFrame)
    }
  }, [mode, readyKey, active, loaded, total, reportReady])

  useEffect(() => {
    if (mode !== 'combat') return
    let cancelled = false
    let frame: number | null = null
    let compileWatchdog: number | null = null
    let stableFrames = 0
    let compiling = false

    const settleReady = () => {
      if (cancelled || reportedKey.current === readyKey) return
      if (compileWatchdog !== null) {
        window.clearTimeout(compileWatchdog)
        compileWatchdog = null
      }
      reportReady()
    }

    const tick = () => {
      if (cancelled || reportedKey.current === readyKey) return
      stableFrames = combatCoreReady(scene) ? stableFrames + 1 : 0
      if (stableFrames >= 2 && !compiling) {
        compiling = true
        try {
          const pending = gl.compileAsync(scene, camera)
          compileWatchdog = window.setTimeout(settleReady, 1200)
          void pending.then(settleReady).catch(settleReady)
        } catch {
          settleReady()
        }
        return
      }
      frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => {
      cancelled = true
      if (frame !== null) cancelAnimationFrame(frame)
      if (compileWatchdog !== null) window.clearTimeout(compileWatchdog)
    }
  }, [gl, scene, camera, mode, readyKey, reportReady])

  return null
}
